import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { useLexicon } from '../../hooks/useLexicon';
import { TeamMatchesTable } from './TeamMatchesTable';
import { SavedFilterTemplatesControlsView } from '../filterTemplates/SavedFilterTemplatesControls';
import { useSavedFilterTemplates } from '../../hooks/useSavedFilterTemplates';
import { ParametersSelector } from '../ParametersSelector';
import searchIcon from '../../images/ic_search.svg';
import closeIcon from '../../images/ic_close.svg';

const visibleScrollbar = css`
  scrollbar-width: auto;
  scrollbar-color: ${(props) => props.theme.green} ${(props) => props.theme.black};

  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.black};
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.green};
    border-radius: 999px;
    border: 3px solid ${(props) => props.theme.black};
  }
`;

export const DropdownWrapper = styled.div`
  position: relative;
  margin-bottom: 30px;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? '0.6' : '1')};
`;

export const OpenedDropdown = styled.div`
  position: absolute;
  z-index: 999;
  width: 100%;
  margin-top: -7px;
  border-radius: 2px;
  opacity: 1;
  background-color: ${(props) => props.theme.blueish3};
  max-height: 200px;
  overflow: scroll;
  border: 1px solid ${(props) => props.theme.borderColor};
  ${visibleScrollbar};

  > div {
    opacity: 1;
    color: ${(props) => props.theme.blueish};
    font-family: 'SatoshiRegular';
    font-size: 16px;
    font-weight: 400;
    font-style: Regular;
    letter-spacing: 0px;
    text-align: left;
    line-height: 24px;
    padding: 10px 6px;
    cursor: pointer;

    &:hover {
      background-color: ${(props) => props.theme.black};
    }
  }
`;

export const ParamCheckboxStyle = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  padding: 10px !important;

  label {
    opacity: 1;
    color: ${(props) => props.theme.blueish} !important;
    font-family: 'SatoshiRegular';
    font-size: 16px;
    font-weight: 400;
    font-style: Regular;
    letter-spacing: 0px;
    text-align: left;
    line-height: 24px;
    width: 100%;
  }

  input[type='checkbox'] {
    appearance: none;
    background-color: transparent;
    margin: 0;
    font: inherit;
    width: 16px;
    height: 16px;
    border: 1px solid ${(props) => props.theme.green};
    border-radius: 2px;
    transform: translateY(-0.075em);
    display: grid;
    place-content: center;
    cursor: pointer;
  }

  input[type='checkbox']::before {
    content: '';
    width: 8px;
    height: 0.6em;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 21%, 80% 0, 43% 62%);
    transform: scale(0);
    transition: transform 0.15s ease-in-out;
    background-color: ${(props) => props.theme.green};
  }

  input[type='checkbox']:checked::before {
    transform: scale(1);
  }
`;

export const ParamsWrap = styled.div`
  position: relative;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? '0.6' : '1')};
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const ClickableStatsHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.blueish};
  font-family: 'SatoshiMedium';
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  opacity: ${(props) => (props.$hidden ? 0 : 1)};
  transform: translateX(${(props) => (props.$hidden ? '-10px' : '0')});
  transition: opacity 220ms ease-in-out, transform 260ms ease-in-out;
  pointer-events: ${(props) => (props.$hidden ? 'none' : 'auto')};
`;

const ClickableStatsHintDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.green};
  box-shadow: 0 0 8px ${(props) => props.theme.green};
  flex-shrink: 0;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
`;

const MatchSearchSlot = styled.div`
  position: relative;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
`;

const MatchSearch = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: ${(props) => (props.$open ? '380px' : '40px')};
  height: 40px;
  border: 1px solid ${(props) => (props.$open ? props.theme.green : props.theme.borderColor)};
  background: ${(props) => props.theme.black};
  transition: width 320ms cubic-bezier(0.16, 1, 0.3, 1), border-color 180ms ease, box-shadow 180ms ease;

  &:focus-within {
    box-shadow: 0 0 0 3px rgba(191, 232, 45, 0.12);
  }
`;

const MatchSearchToggle = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 15px;
    height: 15px;
    filter: ${(props) => (props.$open ? 'brightness(0) saturate(100%) invert(83%) sepia(35%) saturate(630%) hue-rotate(29deg) brightness(103%) contrast(82%)' : 'none')};
    transition: filter 180ms ease, transform 180ms ease;
  }

  &:hover img {
    transform: scale(1.12);
  }
`;

const MatchSearchInput = styled.input`
  min-width: 0;
  width: 100%;
  padding: 0 12px 0 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium';
  font-size: 14px;
  line-height: 20px;
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transform: translateX(${(props) => (props.$open ? '0' : '-8px')});
  transition: opacity 180ms ease 100ms, transform 260ms cubic-bezier(0.16, 1, 0.3, 1) 70ms;

  &::placeholder {
    color: ${(props) => props.theme.blueish2};
  }
`;

const MatchSearchClose = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  margin-right: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transform: ${(props) => (props.$open ? 'scale(1) rotate(0deg)' : 'scale(0.55) rotate(-90deg)')};
  transition: opacity 140ms ease 100ms, transform 220ms cubic-bezier(0.16, 1, 0.3, 1) 80ms;

  img {
    width: 13px;
    height: 13px;
  }

  &:hover img {
    filter: brightness(0) saturate(100%) invert(83%) sepia(35%) saturate(630%) hue-rotate(29deg) brightness(103%) contrast(82%);
  }
`;

const ToolbarTemplateControls = styled.div`
  flex: 1;
  min-width: 0;

  button, button span, button strong {
    font-family: 'SpaceGrotesk' !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    line-height: 24px !important;
    text-transform: uppercase !important;
  }
`;

const SmrtSearchTable = ({
  statMathc,
  team,
  onSeasonChange,
  season,
  loadingStat,
  filterState,
  filterActions,
  templateFilters,
  onApplyTemplate,
}) => {
  const { t } = useTranslation();
  const l = useLexicon();
  const [openEventFilterModal, setOpenEventFilterModal] = useState(false);
  const [isMatchSearchOpen, setIsMatchSearchOpen] = useState(false);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  const matchSearchInputRef = useRef(null);

  const savedTemplates = useSavedFilterTemplates({
    pageKey: 'team_matches',
    filters: templateFilters,
    onApplyTemplate,
    showUpdateButton: false,
  });
  const [loadDownloadXml, setDownloadXml] = useState(false);
  const [loadDownloadXls, setDownloadXls] = useState(false);
  const [tableTypeSorting, setTableTypeSorting] = useState({
    type: 'default',
    param: '',
  });

  useEffect(() => {
    if (isMatchSearchOpen) {
      try {
        matchSearchInputRef.current?.focus({ preventScroll: true });
      } catch {
        matchSearchInputRef.current?.focus();
      }
    }
  }, [isMatchSearchOpen]);

  const toggleMatchSearch = () => {
    setIsMatchSearchOpen((isOpen) => {
      if (isOpen) {
        setMatchSearchTerm('');
      }
      return !isOpen;
    });
  };

  const selectedParameters = filterState?.selectedParameters || {
    checkedIds: [],
    params: [],
  };

  const filteredMatches = useMemo(() => {
    const selectedParameterIds = new Set(
      selectedParameters.checkedIds.map(Number)
    );

    return (statMathc || []).map((match) => ({
      ...match,
      parameters: [...(match?.parameters || [])]
        .filter((parameter) =>
          selectedParameterIds.has(Number(parameter?.parameter_id ?? parameter?.id))
        )
        .sort(
          (a, b) => (a?.parameter_id || a?.id || 0) - (b?.parameter_id || b?.id || 0)
        ),
    }));
  }, [selectedParameters.checkedIds, statMathc]);

  const dowloadXls = async () => {
    setDownloadXls(true);
    try {
      await onSeasonChange?.(season, selectedParameters.checkedIds, 'xls');
    } catch (e) {
      console.log(e);
    } finally {
      setDownloadXls(false);
    }
  };

  const dowloadXml = async () => {
    setDownloadXml(true);
    try {
      await onSeasonChange?.(season, selectedParameters.checkedIds, 'xml');
    } catch (e) {
      console.log(e);
    } finally {
      setDownloadXml(false);
    }
  };

  const handleSortTable = (param) => {
    const paramId = param.parameter_id || param.id;
    if (tableTypeSorting.type === 'asc' && paramId === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'desc', param: paramId });
    } else if (tableTypeSorting.type === 'desc' && paramId === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'default', param: paramId });
    } else {
      setTableTypeSorting({ type: 'asc', param: paramId });
    }
  };

  const sortTable = (a, b) => {
    if (tableTypeSorting.param) {
      const paramA = a.parameters?.find(
        (param) => (param.parameter_id || param.id) === tableTypeSorting.param
      );
      const paramB = b.parameters?.find(
        (param) => (param.parameter_id || param.id) === tableTypeSorting.param
      );
      if (paramA && paramB) {
        if (tableTypeSorting.type === 'asc') {
          return paramA.value - paramB.value;
        }
        if (tableTypeSorting.type === 'desc') {
          return paramB.value - paramA.value;
        }
        return new Date(b.date) - new Date(a.date);
      }
      if (paramA) return -1;
      if (paramB) return 1;
    }
    return new Date(b.date) - new Date(a.date);
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 30 }}>
        <ToolbarRow>
          <ClickableStatsHint $hidden={isMatchSearchOpen} aria-hidden={isMatchSearchOpen}>
            <ClickableStatsHintDot />
            {t('smrtSearch.clickableStatsHint')}
          </ClickableStatsHint>
          <ToolbarActions>
            <MatchSearchSlot>
              <MatchSearch $open={isMatchSearchOpen}>
                <MatchSearchToggle
                  type='button'
                  $open={isMatchSearchOpen}
                  onClick={() => setIsMatchSearchOpen(true)}
                  aria-label='Search matches by opponent'
                  title='Search matches by opponent'
                >
                  <img src={searchIcon} alt='' />
                </MatchSearchToggle>
                <MatchSearchInput
                  ref={matchSearchInputRef}
                  $open={isMatchSearchOpen}
                  value={matchSearchTerm}
                  onChange={(event) => setMatchSearchTerm(event.target.value)}
                  placeholder='Search opponent or parameter…'
                  aria-label='Search matches by opponent'
                />
                <MatchSearchClose
                  type='button'
                  $open={isMatchSearchOpen}
                  onClick={toggleMatchSearch}
                  aria-label='Close opponent search'
                  tabIndex={isMatchSearchOpen ? 0 : -1}
                >
                  <img src={closeIcon} alt='' />
                </MatchSearchClose>
              </MatchSearch>
            </MatchSearchSlot>
            <button
              className='buttonFilter'
              style={{ width: 'fit-content', padding: '0 5px' }}
              onClick={dowloadXml}
            >
              {loadDownloadXml ? l(312) : l(339)}
            </button>
            <button
              className='buttonFilter'
              style={{ width: 'fit-content', padding: '0 5px' }}
              onClick={dowloadXls}
            >
              {loadDownloadXls ? l(312) : l(14)}
            </button>
            <ToolbarTemplateControls>
              <SavedFilterTemplatesControlsView
                {...savedTemplates}
                pageKey="team_matches"
                showCreateButton={false}
                showUpdateButton={false}
                showStatusMessage={false}
                showModals={false}
              />
            </ToolbarTemplateControls>
            <button
              className='buttonFilter'
              style={{ width: 'fit-content', padding: '0 5px' }}
              onClick={() => setOpenEventFilterModal(true)}
            >
              {t('smrtSearch.eventFilter')}
            </button>
          </ToolbarActions>
        </ToolbarRow>
        <TeamMatchesTable
          currentTeam={team}
          data={[...filteredMatches].sort(sortTable)}
          opponentSearchTerm={matchSearchTerm}
          loading={loadingStat}
          handleSortTable={handleSortTable}
          tableTypeSorting={tableTypeSorting}
          tempSelectedFilters={selectedParameters}
        />
      </div>

      <ParametersSelector
        open={openEventFilterModal}
        onClose={() => setOpenEventFilterModal(false)}
        type='team'
        updateData={filterActions?.changeParameters}
        savedTemplatesState={savedTemplates}
        savedTemplatesPageKey='team_matches'
        syncedParameterGroups={filterState?.parameterGroups}
      />
    </>
  );
};

export default SmrtSearchTable;
